import { z } from "zod";

export type { OIDCConfig, SAMLConfig } from "@better-auth/sso";

const httpsUrl = z.url({ protocol: /^https$/, message: "Must be a valid HTTPS URL" });

// --- OIDC ---

const tokenEndpointAuthentication = z.enum([
  "client_secret_basic",
  "client_secret_post",
  "private_key_jwt", // Not supported in UI yet since need to show privateKeyAlgorithm & privateKeyId when selected
]);

export const oidcConfigSchema = z.object({
  clientId: z
    .string()
    // Required only on registration, optional on edit so rule needs to allow empty string for the edit form
    .refine((v) => v.length === 0 || v.length >= 5, "Client ID must be at least 5 characters long"),
  clientSecret: z.string().optional(),
  tokenEndpointAuthentication: tokenEndpointAuthentication.optional(),
  scopes: z.array(z.string()).optional(),
  pkce: z.boolean().optional(),
  skipDiscovery: z.boolean().optional(),
  authorizationEndpoint: httpsUrl.optional(),
  tokenEndpoint: httpsUrl.optional(),
  userInfoEndpoint: httpsUrl.optional(),
  jwksEndpoint: httpsUrl.optional(),
});

export type OidcConfigInput = z.infer<typeof oidcConfigSchema>;

const endpointFields = [
  "authorizationEndpoint",
  "tokenEndpoint",
  "userInfoEndpoint",
  "jwksEndpoint",
] as const;

const oidcFormBase = oidcConfigSchema
  .omit({ scopes: true })
  .required({
    clientSecret: true,
    pkce: true,
  })
  .extend({
    skipDiscovery: z.boolean().optional(),
    authorizationEndpoint: z.union([httpsUrl, z.literal("")]).optional(),
    tokenEndpoint: z.union([httpsUrl, z.literal("")]).optional(),
    userInfoEndpoint: z.union([httpsUrl, z.literal("")]).optional(),
    jwksEndpoint: z.union([httpsUrl, z.literal("")]).optional(),
    issuer: z
      .httpUrl("Must be a valid URL")
      .refine((v) => !v.endsWith("/"), "Issuer URL must not end with a trailing slash"),
    scopes: z.string(),
  });

function skipDiscoveryRefine(data: z.infer<typeof oidcFormBase>, ctx: z.RefinementCtx) {
  if (data.skipDiscovery === true) {
    for (const field of endpointFields) {
      if (!data[field]) {
        ctx.addIssue({
          code: "custom",
          message: "Required when discovery is disabled",
          path: [field],
        });
      }
    }
  }
}

export const oidcFormSchema = oidcFormBase.superRefine(skipDiscoveryRefine);

export const oidcFormCreateSchema = oidcFormBase
  .extend({
    clientId: z.string().min(5, "Client ID must be at least 5 characters"),
    clientSecret: z.string().min(1, "Client secret is required"),
  })
  .superRefine(skipDiscoveryRefine);

export type OidcFormValues = z.infer<typeof oidcFormBase>;

// --- SAML ---

export const signatureAlgorithm = z.enum([
  "rsa-sha256",
  "rsa-sha384",
  "rsa-sha512",
  "ecdsa-sha256",
  "ecdsa-sha384",
  "ecdsa-sha512",
]);

export const digestAlgorithm = z.enum(["sha256", "sha384", "sha512"]);

export const identifierFormat = z.enum([
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
  "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:transient",
]);

export const identifierFormatLabels: Record<z.infer<typeof identifierFormat>, string> = {
  "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress": "Email address",
  "urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified": "Unspecified",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:persistent": "Persistent",
  "urn:oasis:names:tc:SAML:2.0:nameid-format:transient": "Transient",
};

const samlMappingSchema = z.object({
  email: z.string(),
  name: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

export const samlConfigSchema = z.object({
  entryPoint: z.url({ protocol: /^https$/ }),
  cert: z.string(),
  idpMetadata: z.union([
    z.object({ metadata: z.string().min(1), entityID: z.string().optional() }),
    z.object({ metadata: z.undefined().optional(), entityID: z.string().min(1) }),
  ]),
  wantAssertionsSigned: z.boolean(),
  authnRequestsSigned: z.boolean(),
  signatureAlgorithm: signatureAlgorithm,
  digestAlgorithm: digestAlgorithm,
  identifierFormat: identifierFormat,
  privateKey: z.string(),
  mapping: samlMappingSchema,
});

export type SamlConfigInput = z.infer<typeof samlConfigSchema>;

const samlFormBaseSchema = z.object({
  issuer: z.url("Must be a valid URL").nonempty("Issuer is required"), // urn:stotles:saml:<company guid>:<provider> - urn: is a valid URL scheme, so this is a valid URL
  configMode: z.enum(["metadata", "manual"]),
  idpMetadataXml: z.string(),
  idpEntityId: z.string(),
  entryPoint: z.union([httpsUrl, z.literal("")]),
  cert: z
    .string()
    .regex(
      /^(\s*-----BEGIN CERTIFICATE-----\s+[A-Za-z0-9+/=\s]+-----END CERTIFICATE-----\s*)?$/,
      "Must be a PEM-encoded certificate (-----BEGIN CERTIFICATE----- ... -----END CERTIFICATE-----)",
    ),
  wantAssertionsSigned: z.boolean(),
  authnRequestsSigned: z.boolean(),
  privateKey: z
    .string()
    .regex(
      /^(\s*-----BEGIN (RSA |EC )?PRIVATE KEY-----\s+[A-Za-z0-9+/=\s]+-----END (RSA |EC )?PRIVATE KEY-----\s*)?$/,
      "Must be a PEM-encoded private key (-----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----)",
    ),
  signatureAlgorithm: signatureAlgorithm,
  digestAlgorithm: digestAlgorithm,
  identifierFormat: identifierFormat,
  mappingEmail: z.string(),
  mappingName: z.string(),
  mappingFirstName: z.string(),
  mappingLastName: z.string(),
});

export type SamlFormValues = z.infer<typeof samlFormBaseSchema>;

function samlFormRefine(data: SamlFormValues, ctx: z.RefinementCtx) {
  if (data.authnRequestsSigned && !data.privateKey) {
    ctx.addIssue({
      code: "custom",
      message: "Private key is required when signing authentication requests",
      path: ["privateKey"],
    });
  }

  const hasAnyMapping =
    data.mappingEmail || data.mappingName || data.mappingFirstName || data.mappingLastName;
  if (hasAnyMapping) {
    if (!data.mappingEmail) {
      ctx.addIssue({
        code: "custom",
        message: "Email attribute is required when setting attribute mapping",
        path: ["mappingEmail"],
      });
    }
    if (!data.mappingName && !(data.mappingFirstName && data.mappingLastName)) {
      ctx.addIssue({
        code: "custom",
        message: "Provide either a display name attribute or both first and last name attributes",
        path: ["mappingName"],
      });
    }
  }

  if (data.configMode === "metadata" && !data.idpMetadataXml) {
    ctx.addIssue({
      code: "custom",
      message: "IdP metadata XML is required",
      path: ["idpMetadataXml"],
    });
  }

  if (data.configMode === "manual" && !data.entryPoint) {
    ctx.addIssue({
      code: "custom",
      message: "Single sign-on URL is required",
      path: ["entryPoint"],
    });
  }
}

export const samlFormSchema = samlFormBaseSchema.superRefine(samlFormRefine);

export const samlFormCreateSchema = samlFormBaseSchema
  .superRefine(samlFormRefine)
  .superRefine((data, ctx) => {
    const hasAnyMapping =
      data.mappingEmail || data.mappingName || data.mappingFirstName || data.mappingLastName;
    if (!hasAnyMapping) {
      ctx.addIssue({
        code: "custom",
        message: "Attribute mapping is required",
        path: ["mappingEmail"],
      });
    }

    if (data.configMode === "manual") {
      if (!data.cert) {
        ctx.addIssue({
          code: "custom",
          message: "Signing certificate is required",
          path: ["cert"],
        });
      }
      if (!data.idpEntityId) {
        ctx.addIssue({
          code: "custom",
          message: "Identity Provider Identifier is required",
          path: ["idpEntityId"],
        });
      }
    }
  });

// --- Shared API schemas ---

export const registerSsoProviderSchema = z.object({
  providerId: z.string(),
  issuer: z.url(),
  domain: z.string(),
  organizationId: z.uuid(),
  oidcConfig: oidcConfigSchema.optional(),
  samlConfig: samlConfigSchema.optional(),
  overrideUserInfo: z.boolean().optional(),
});

export type RegisterSsoProviderInput = z.infer<typeof registerSsoProviderSchema>;

export const registerSsoProviderResponseSchema = z.object({
  providerId: z.string(),
  redirectURI: httpsUrl,
  domain: z.string(),
  domainVerified: z.boolean(),
  domainVerificationToken: z.string(),
});

export type RegisterSsoProviderResponse = z.infer<typeof registerSsoProviderResponseSchema>;

export const updateSsoProviderSchema = z.object({
  providerId: z.string(),
  issuer: z.url().optional(),
  domain: z.string().optional(),
  oidcConfig: oidcConfigSchema.partial().optional(),
  samlConfig: samlConfigSchema.partial().optional(),
});

export type UpdateSsoProviderInput = z.infer<typeof updateSsoProviderSchema>;
